import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthCard } from '../components/AuthCard';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Divider } from '../components/Divider';
import { Alert } from '../components/Alert';

const PLAN_LABELS: Record<string, string> = {
  pro: 'Pro',
  team: 'Team',
};

function getRedirectUrl(plan: string | null): string {
  const callbackUrl = new URL('/callback', window.location.origin);
  if (plan && (plan === 'pro' || plan === 'team')) {
    callbackUrl.searchParams.set('plan', plan);
  }
  return callbackUrl.toString();
}

export function SignUp() {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');
  const planLabel = plan ? (PLAN_LABELS[plan] ?? null) : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);

  async function handleGitHubSignUp() {
    setError(null);
    setGithubLoading(true);
    try {
      if (plan && (plan === 'pro' || plan === 'team')) {
        localStorage.setItem('layerlint_pending_plan', plan);
      }
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: { redirectTo: getRedirectUrl(plan) },
      });
      if (oauthError) {
        setError(oauthError.message);
        setGithubLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setGithubLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      if (plan && (plan === 'pro' || plan === 'team')) {
        localStorage.setItem('layerlint_pending_plan', plan);
      }

      const { error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: getRedirectUrl(plan),
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setLoading(false);
    }
  }

  return (
    <AuthCard title="Create your account" subtitle="Start fixing Dockerfiles automatically">
      {planLabel && (
        <div className="mb-4 inline-flex items-center gap-2 bg-ll-cyan/10 text-ll-cyan text-xs font-semibold px-3 py-1.5 rounded-full">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="2 6 5 9 10 3" />
          </svg>
          Signing up for {planLabel} plan
        </div>
      )}

      {error && <Alert variant="error" message={error} onDismiss={() => setError(null)} />}

      {success ? (
        <>
          <Alert
            variant="success"
            message={
              planLabel
                ? `Check your email to verify your account. After verification, you'll be redirected to complete your ${planLabel} plan checkout.`
                : 'Check your email to verify your account.'
            }
          />
          <p className="text-center text-sm text-ll-muted mt-4">
            <Link to="/sign-in" className="text-ll-cyan hover:brightness-110 transition-colors">
              Back to sign in
            </Link>
          </p>
        </>
      ) : (
        <>
          <Button
            variant="github"
            onClick={handleGitHubSignUp}
            loading={githubLoading}
            disabled={loading}
          >
            Continue with GitHub
          </Button>

          <Divider />

          <form onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              required
              disabled={loading}
            />
            <Input
              label="Password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              required
              disabled={loading}
            />
            <p className="text-xs text-ll-muted -mt-2 mb-4">At least 8 characters</p>
            <Input
              label="Confirm Password"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              required
              disabled={loading}
            />

            <Button type="submit" loading={loading} disabled={githubLoading}>
              Create account
            </Button>
          </form>

          <p className="text-center text-sm text-ll-muted mt-6">
            Already have an account?{' '}
            <Link to="/sign-in" className="text-ll-cyan hover:brightness-110 transition-colors">
              Sign in
            </Link>
          </p>
        </>
      )}
    </AuthCard>
  );
}
